import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { api } from "@/lib/api-client";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import _ from "lodash";
import { useState } from "react";
import { useSearchParams } from "react-router";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { cn } from "@/utils";
import { Divider, Skeleton } from "@heroui/react";

export const GeneralTab = () => {
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");

  const [selectedDay, setSelectedDay] = useState<string | undefined>(undefined);

  const {
    data: analyticsData,
    processedErrorMessage: fetchAnalyticsErrorMessage,
  } = api.stripe.useGetStripeAnalytics({
    startDate: startDate ?? undefined,
    endDate: endDate ?? undefined,
  });

  const isMobile = useMediaQuery("(max-width: 768px)");

  let yWidth = 45;
  const max =
    _.max(
      analyticsData?.chart.map((item) => item.value.toString().length ?? 3) ?? [
        3,
      ],
    ) ?? 3;
  if (max > 3) yWidth = 75;
  else if (max > 2) yWidth = 60;
  else if (max > 1) yWidth = 45;

  return (
    <div>
      <LimeAlert message={fetchAnalyticsErrorMessage} className="mb-2" />

      {isMobile && (
        <div className="mb-2">
          <InfoBlock
            data={[
              {
                label: t`Skupaj prihodki`,
                values:
                  analyticsData?.general.totalIncomeByCurrency.map(
                    (item) => item.formattedAmount,
                  ) ?? [],
              },
              {
                label: t`Št. plačil`,
                values: [`${analyticsData?.general.numPayments}`],
              },
              {
                label: t`Št. strank`,
                values: [`${analyticsData?.general.numUniqueCustomers}`],
              },
            ]}
          />
        </div>
      )}

      {isMobile && (
        <p className="mb-2 text-sm font-semibold">
          <Trans>Grafični prikaz prihodkov</Trans>
        </p>
      )}

      <div className="flex w-full flex-row gap-4">
        <div className="flex-1 rounded-lg border-1 border-[#D8D8D8] px-1 py-5 md:border-none">
          <Skeleton isLoaded={analyticsData != null} className="rounded-lg">
            <ResponsiveContainer
              width={"100%"}
              height={300}
              className={"chart-container"}
            >
              <LineChart
                data={
                  analyticsData?.chart.map((item) => ({
                    ...item,
                    date: dayjs(item.date).format("DD. MM."),
                    fullDate: item.date,
                  })) ?? []
                }
                onClick={(e) => {
                  setSelectedDay(e?.activePayload?.[0].payload.fullDate);
                }}
                margin={{
                  left: 0,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  padding={{ left: 25, right: 25 }}
                  tickMargin={7}
                  axisLine={{
                    stroke: "var(--border)",
                    strokeWidth: 2,
                  }}
                />
                <YAxis
                  dataKey={"value"}
                  width={yWidth}
                  axisLine={{
                    stroke: "var(--border)",
                    strokeWidth: 2,
                  }}
                  tickLine={false}
                  tickMargin={7}
                />
                {isMobile ? (
                  <Tooltip content={() => <></>} />
                ) : (
                  <Tooltip
                    cursor={{
                      strokeWidth: 2,
                      strokeDasharray: "3 3",
                    }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="rounded-lg bg-white py-1 shadow-lg">
                            <p className="px-2 py-1 text-center text-xs text-[#8C8C8C]">
                              {_.capitalize(
                                dayjs(payload[0].payload.fullDate)
                                  .locale("sl")
                                  .format("dddd, DD. MMMM"),
                              ) ?? ""}
                            </p>
                            <Divider />
                            <p className="px-2 py-1 text-center text-sm">
                              {payload[0].payload.formattedValue}
                            </p>
                          </div>
                        );
                      }

                      return null;
                    }}
                  />
                )}
                <Line
                  type="linear"
                  dataKey="value"
                  stroke="#548F68"
                  strokeWidth={3}
                  gradientUnits="userSpaceOnUse"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Skeleton>
        </div>

        {!isMobile && (
          <div className="flex w-64 flex-col gap-2">
            <Skeleton isLoaded={analyticsData != null} className="rounded-lg">
              <InfoBlock
                data={[
                  {
                    label: t`Št. plačil`,
                    values:
                      analyticsData?.general.totalIncomeByCurrency.map(
                        (item) => item.formattedAmount,
                      ) ?? [],
                  },
                ]}
              />
            </Skeleton>

            <Skeleton isLoaded={analyticsData != null} className="rounded-lg">
              <InfoBlock
                data={[
                  {
                    label: t`Št. plačil`,
                    values: [`${analyticsData?.general.numPayments}`],
                  },
                ]}
              />
            </Skeleton>

            <Skeleton isLoaded={analyticsData != null} className="rounded-lg">
              <InfoBlock
                data={[
                  {
                    label: t`Št. strank`,
                    values: [`${analyticsData?.general.numUniqueCustomers}`],
                  },
                ]}
              />
            </Skeleton>
          </div>
        )}
      </div>

      {isMobile && (
        <div className="mt-2">
          <InfoBlock
            data={[
              {
                label:
                  _.capitalize(
                    dayjs(selectedDay)
                      .locale("sl")
                      .format("dddd, DD. MMMM YYYY"),
                  ) ?? "",
                values:
                  selectedDay == undefined
                    ? [t`Dan ni izbran`]
                    : [
                        analyticsData?.chart.find(
                          (item) => item.date == selectedDay,
                        )?.formattedValue ?? t`Ni podatkov`,
                      ],
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

const InfoBlock = ({
  data,
}: {
  data: {
    label: string;
    values: string[];
  }[];
}) => (
  <div
    className={cn(
      "flex flex-row justify-center rounded-lg border-1 border-[#D8D8D8] p-5",
      {
        "justify-between": data.length > 1,
      },
    )}
  >
    {data.map((item, i) => (
      <div className="flex flex-col items-center justify-center gap-1" key={i}>
        <p className="text-sm text-[#8C8C8C]">{item.label}</p>
        {item.values.map((value, id) => (
          <p className="text-xl font-semibold" key={id}>
            {value}
          </p>
        ))}
      </div>
    ))}
  </div>
);
